/**
 * Returns the last underscore-delimited segment from a nested selection id.
 */
const getLastSegment = (nestedId: string): string => {
  const parts = String(nestedId).split('_');
  return parts[parts.length - 1] ?? '';
};

/**
 * Returns the display-friendly leaf id from a node id.
 * Handles ids with list prefixes (".") and nested segments ("_" or "depth:id").
 */
export const getDisplayIdFromNodeId = (nodeId: string): string => {
  const lastSegment = getLastSegment(String(nodeId));
  const dotParts = lastSegment.split('.');
  const dotLast = dotParts[dotParts.length - 1] ?? lastSegment;
  if (dotLast.includes(':')) {
    const segParts = dotLast.split(':');
    return segParts[segParts.length - 1] ?? dotLast;
  }
  return dotLast;
};

export const copyTextToClipboard = async (text: string): Promise<boolean> => {
  if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      return false;
    }
  }

  if (typeof document === 'undefined' || !document.body) {
    return false;
  }

  const textArea = document.createElement('textarea');
  textArea.value = text;
  textArea.setAttribute('readonly', 'true');
  textArea.style.position = 'absolute';
  textArea.style.left = '-9999px';
  document.body.appendChild(textArea);
  textArea.select();

  const isCopied = document.execCommand('copy');
  document.body.removeChild(textArea);

  return isCopied;
};

type SelectOption = {
  value: string;
  label: string;
};

export const sortSelectedOptionsFirst = <T extends SelectOption>(
  options: readonly T[],
  selectedValues: ReadonlySet<string>
): T[] =>
  options
    .map((option, index) => ({ option, index }))
    .sort((a, b) => {
      const aSelected = selectedValues.has(a.option.value);
      const bSelected = selectedValues.has(b.option.value);
      if (aSelected !== bSelected) return aSelected ? -1 : 1;

      if (!aSelected) {
        const aIsOther = a.option.label === 'Other';
        const bIsOther = b.option.label === 'Other';
        if (aIsOther !== bIsOther) return aIsOther ? 1 : -1;
      }

      return a.index - b.index;
    })
    .map(({ option }) => option);
